import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ScrollText, UsersRound, Save, Bell, Loader2, ArrowLeft, History, RotateCcw, Eye, EyeOff, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

const ICONS = {
  'shield': Shield,
  'scroll-text': ScrollText,
  'users-round': UsersRound,
};

export default function AdminLegalDocumentsPage() {
  const { colors } = useAdminTheme();
  const [documents, setDocuments] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/legal/documents`);
      setDocuments(res.data);
    } catch {
      toast.error('Failed to load legal documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  return (
    <AdminLayout>
      <div className="max-w-4xl" data-testid="admin-legal-page">
        {selectedSlug ? (
          <DocumentEditor
            slug={selectedSlug}
            onBack={() => { setSelectedSlug(null); fetchDocuments(); }}
            colors={colors}
          />
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-xl font-bold" style={{ color: colors.text }}>Legal Documents</h1>
              <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
                Manage policies and notify users when they change
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} /></div>
            ) : (
              <div className="space-y-3">
                {documents.map(doc => {
                  const IconComp = ICONS[doc.icon] || FileText;
                  return (
                    <button
                      key={doc.slug}
                      onClick={() => setSelectedSlug(doc.slug)}
                      className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:scale-[1.01]"
                      style={{ background: colors.card, border: `1px solid ${colors.border}` }}
                      data-testid={`legal-doc-${doc.slug}`}
                    >
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${colors.accent}15` }}>
                        <IconComp className="h-5 w-5" style={{ color: colors.accent }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm" style={{ color: colors.text }}>{doc.title}</p>
                        <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                          {doc.updated_at
                            ? `Last updated: ${new Date(doc.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                            : 'Not yet configured'}
                          {doc.version > 0 && ` \u00B7 v${doc.version}`}
                        </p>
                      </div>
                      <ArrowLeft className="h-4 w-4 rotate-180" style={{ color: colors.textMuted }} />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function DocumentEditor({ slug, onBack, colors }) {
  const [doc, setDoc] = useState(null);
  const [content, setContent] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
  const [showPublishDefaultConfirm, setShowPublishDefaultConfirm] = useState(false);
  const [publishingDefault, setPublishingDefault] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Which slugs have a backend-rendered default template available
  const SLUGS_WITH_DEFAULTS = ['privacy-policy', 'terms-of-use'];
  const hasDefault = SLUGS_WITH_DEFAULTS.includes(slug);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${API}/legal/${slug}`, { withCredentials: true });
        setDoc(res.data);
        setContent(res.data.content || '');
      } catch {
        toast.error('Failed to load document');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/legal/${slug}/history`, { withCredentials: true });
      setHistory(res.data);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSave = async (notify) => {
    setShowNotifyConfirm(false);
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/legal/${slug}`, {
        content,
        notify_users: notify,
        change_summary: changeSummary,
      }, { withCredentials: true });

      toast.success(
        notify
          ? `Saved v${res.data.version} \u2014 ${res.data.notifications_sent} users notified`
          : `Saved v${res.data.version}`
      );
      setDoc(prev => ({ ...prev, version: res.data.version, updated_at: res.data.updated_at, updated_by: res.data.updated_by }));
      setChangeSummary('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (version) => {
    try {
      const res = await axios.post(`${API}/admin/legal/${slug}/restore/${version}`, {}, { withCredentials: true });
      toast.success(`Restored from v${version} \u2192 now v${res.data.version}`);
      // Reload
      const docRes = await axios.get(`${API}/legal/${slug}`, { withCredentials: true });
      setDoc(docRes.data);
      setContent(docRes.data.content || '');
      setShowHistory(false);
      fetchHistory();
    } catch {
      toast.error('Failed to restore version');
    }
  };

  const handlePublishDefault = async () => {
    setShowPublishDefaultConfirm(false);
    setPublishingDefault(true);
    try {
      const res = await axios.post(
        `${API}/admin/legal/${slug}/publish-default`,
        {},
        { withCredentials: true }
      );
      toast.success(`Default published as v${res.data.version} (${res.data.length.toLocaleString()} chars)`);
      // Reload the doc so the editor shows the fresh content
      const docRes = await axios.get(`${API}/legal/${slug}`, { withCredentials: true });
      setDoc(docRes.data);
      setContent(docRes.data.content || '');
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to publish default content');
    } finally {
      setPublishingDefault(false);
    }
  };

  const handleLoadDefaultIntoEditor = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/legal/${slug}/default-preview`,
        { withCredentials: true }
      );
      setContent(res.data.content);
      toast.success(`Loaded default (${res.data.length.toLocaleString()} chars) — review then click Save`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load default content');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} /></div>;
  }

  const hasChanges = content !== (doc?.content || '');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full h-9 w-9 p-0" data-testid="legal-editor-back">
          <ArrowLeft className="h-4 w-4" style={{ color: colors.text }} />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold" style={{ color: colors.text }}>{doc?.title}</h2>
          <p className="text-xs" style={{ color: colors.textMuted }}>
            {doc?.updated_at
              ? `v${doc.version} \u00B7 Updated ${new Date(doc.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} by ${doc.updated_by || 'system'}`
              : 'No content yet'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHistory(); }}
          className="gap-1.5"
          style={{ borderColor: colors.border, color: colors.text }}
          data-testid="legal-history-btn"
        >
          <History className="h-3.5 w-3.5" />
          History
        </Button>
      </div>

      {/* Publish Default Content — only for slugs with a backend-rendered fallback.
          Prominent banner when the doc is empty, compact secondary action otherwise. */}
      {hasDefault && (
        <div
          className="rounded-xl p-4"
          style={{
            background: !doc?.content ? '#10b98115' : colors.card,
            border: `1px solid ${!doc?.content ? '#10b981' : colors.border}`,
          }}
          data-testid="legal-publish-default-card"
        >
          <div className="flex items-start gap-3">
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: '#10b98120' }}
            >
              <Download className="h-4 w-4" style={{ color: '#10b981' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: colors.text }}>
                {!doc?.content ? 'Publish Default Content' : 'Republish Default Content'}
              </p>
              <p className="text-xs mt-0.5 mb-3" style={{ color: colors.textMuted }}>
                {!doc?.content
                  ? 'No admin-managed content yet — users are seeing the app\u2019s built-in fallback. Publish the canonical English default now so you can edit it here.'
                  : 'Reset this document to the app\u2019s canonical English default. Current content will be archived to version history.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowPublishDefaultConfirm(true)}
                  disabled={publishingDefault}
                  className="gap-1.5"
                  style={{ background: '#10b981', color: '#fff' }}
                  data-testid="legal-publish-default-btn"
                >
                  {publishingDefault ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  {publishingDefault ? 'Publishing\u2026' : 'Publish Default'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleLoadDefaultIntoEditor}
                  disabled={publishingDefault}
                  className="gap-1.5"
                  style={{ borderColor: colors.border, color: colors.text }}
                  data-testid="legal-load-default-btn"
                >
                  <Eye className="h-3.5 w-3.5" />
                  Load into Editor (review before saving)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version History Panel */}
      {showHistory && (
        <div className="rounded-xl p-4 space-y-3" style={{ background: colors.card, border: `1px solid ${colors.border}` }}>
          <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Version History</h3>
          {historyLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" style={{ color: colors.accent }} />
          ) : history.length === 0 ? (
            <p className="text-xs" style={{ color: colors.textMuted }}>No previous versions</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map(v => (
                <div key={v.version} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: `${colors.accent}08`, border: `1px solid ${colors.border}` }}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium" style={{ color: colors.text }}>
                      v{v.version} — {new Date(v.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs truncate" style={{ color: colors.textMuted }}>
                      by {v.updated_by || 'system'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRestore(v.version)}
                    className="gap-1 text-xs h-7 px-2"
                    style={{ color: colors.accent }}
                    data-testid={`restore-v${v.version}`}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editor / Preview Toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={showPreview ? "outline" : "default"}
          size="sm"
          onClick={() => setShowPreview(false)}
          className="gap-1.5 text-xs"
          data-testid="legal-edit-tab"
        >
          Edit
        </Button>
        <Button
          variant={showPreview ? "default" : "outline"}
          size="sm"
          onClick={() => setShowPreview(true)}
          className="gap-1.5 text-xs"
          style={!showPreview ? { borderColor: colors.border, color: colors.text } : {}}
          data-testid="legal-preview-tab"
        >
          {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          Preview
        </Button>
        {hasChanges && (
          <span className="text-xs ml-2 px-2 py-0.5 rounded-full" style={{ background: '#f59e0b20', color: '#f59e0b' }}>
            Unsaved changes
          </span>
        )}
      </div>

      {/* Editor or Preview */}
      {showPreview ? (
        <div
          className="rounded-xl p-6 min-h-[400px] prose prose-sm max-w-none"
          style={{ background: colors.card, border: `1px solid ${colors.border}`, color: colors.text }}
          dangerouslySetInnerHTML={{ __html: content || '<p style="opacity:0.5">No content yet. Switch to Edit to add content.</p>' }}
          data-testid="legal-preview"
        />
      ) : (
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full rounded-xl p-4 min-h-[400px] text-sm font-mono resize-y focus:outline-none focus:ring-2"
          style={{
            background: colors.card,
            border: `1px solid ${colors.border}`,
            color: colors.text,
            focusRingColor: colors.accent,
          }}
          placeholder="Enter HTML content for this document..."
          data-testid="legal-content-editor"
        />
      )}

      {/* Change Summary */}
      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: colors.textMuted }}>
          Change summary (shown in notification)
        </label>
        <input
          type="text"
          value={changeSummary}
          onChange={(e) => setChangeSummary(e.target.value)}
          placeholder="e.g., Updated data retention section"
          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
          style={{ background: colors.card, border: `1px solid ${colors.border}`, color: colors.text }}
          data-testid="legal-change-summary"
        />
      </div>

      {/* Save Actions */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          onClick={() => handleSave(false)}
          disabled={saving || !hasChanges}
          className="gap-2"
          data-testid="legal-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowNotifyConfirm(true)}
          disabled={saving || !hasChanges}
          className="gap-2"
          style={{ borderColor: colors.border, color: colors.text }}
          data-testid="legal-save-notify-btn"
        >
          <Bell className="h-4 w-4" />
          Save & Notify Users
        </Button>
      </div>

      {/* Notify Confirmation Modal */}
      {showNotifyConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl p-6 max-w-md w-full shadow-2xl" style={{ background: colors.card, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: '#f59e0b20' }}>
                <Bell className="h-5 w-5" style={{ color: '#f59e0b' }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: colors.text }}>Notify all users?</h3>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  This will send a push notification about the {doc?.title} update.
                </p>
              </div>
            </div>

            {changeSummary && (
              <div className="rounded-lg p-3 mb-4 text-sm" style={{ background: `${colors.accent}08`, border: `1px solid ${colors.border}`, color: colors.text }}>
                <span className="text-xs font-medium" style={{ color: colors.textMuted }}>Notification message:</span>
                <p className="mt-1">{changeSummary || `Our ${doc?.title} has been updated. Please review the changes.`}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifyConfirm(false)}
                data-testid="notify-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => handleSave(true)}
                className="gap-1.5"
                data-testid="notify-confirm-btn"
              >
                <Bell className="h-3.5 w-3.5" />
                Save & Notify
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Default Confirmation Modal */}
      {showPublishDefaultConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl p-6 max-w-md w-full shadow-2xl" style={{ background: colors.card, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: '#10b98120' }}>
                <Download className="h-5 w-5" style={{ color: '#10b981' }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: colors.text }}>
                  {doc?.content ? 'Replace current content?' : 'Publish default content?'}
                </h3>
                <p className="text-xs" style={{ color: colors.textMuted }}>
                  {doc?.content
                    ? `Current v${doc?.version || 0} will be archived; a fresh version will be created from the app's built-in default.`
                    : 'The app\u2019s built-in English default will be saved as v1 of this document.'}
                </p>
              </div>
            </div>

            <div className="rounded-lg p-3 mb-4 text-xs" style={{ background: `${colors.accent}08`, border: `1px solid ${colors.border}`, color: colors.textMuted }}>
              After publishing, you can freely edit the content in the editor below. Users will see your edits immediately on page refresh.
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPublishDefaultConfirm(false)}
                data-testid="publish-default-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handlePublishDefault}
                className="gap-1.5"
                style={{ background: '#10b981', color: '#fff' }}
                data-testid="publish-default-confirm-btn"
              >
                <Download className="h-3.5 w-3.5" />
                {doc?.content ? 'Replace & Publish' : 'Publish Default'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
